<template>
  <div class="min-h-screen">
    <PageSEO
      :title="`${project.name} - Project Case Study`"
      :description="project.objective || `Learn about our ${project.name} project. Professional web development and digital solutions.`"
      :keywords="[project.name, 'project case study', 'web development', project.type, 'portfolio']"
      :url="`https://vedware.com/projects/${project.slug}`"
      type="article"
      :published-time="project.date"
      :modified-time="project.date"
    />

    <HeroSection
      :icon="PhFolderOpen"
      :title="project.name"
      :description="project.subtitle || project.objective || 'Professional web development project showcasing modern technologies and best practices.'"
      :meta-info="projectMetaInfo"
    >
      <StoreButtons v-if="hasStoreLinks" :links="project.links!" class="justify-center" />
    </HeroSection>

    <!-- Gallery: native scroll-snap strip, click opens the lightbox -->
    <div class="py-12 sm:py-16">
      <div class="container mx-auto">
        <div class="mx-auto max-w-7xl">
          <div class="relative">
            <ul
              ref="gallery"
              class="gallery -mx-4 flex snap-x snap-mandatory items-center gap-4 overflow-x-auto px-4 pb-4 sm:mx-0 sm:px-0"
              @scroll.passive="updateArrows"
            >
              <li v-for="(image, idx) in project.images" :key="image.src" class="shrink-0 snap-center">
                <button type="button" class="block" :aria-label="`Open image ${idx + 1} of ${project.images.length}`" @click="index = idx">
                  <img
                    :src="image.src"
                    :alt="image.alt"
                    draggable="false"
                    class="h-72 w-auto rounded-xl shadow-soft transition-transform duration-300 hover:scale-[1.02] sm:h-[26rem]"
                  >
                </button>
              </li>
            </ul>

            <template v-if="project.images.length > 1">
              <button
                v-show="canScrollPrev"
                type="button"
                aria-label="Previous images"
                class="gallery-arrow left-2 sm:-left-5"
                @click="scrollGallery(-1)"
              >
                <PhCaretLeft weight="bold" class="size-5" />
              </button>
              <button
                v-show="canScrollNext"
                type="button"
                aria-label="Next images"
                class="gallery-arrow right-2 sm:-right-5"
                @click="scrollGallery(1)"
              >
                <PhCaretRight weight="bold" class="size-5" />
              </button>
            </template>
          </div>

          <p v-if="project.images.length > 1" class="mt-2 text-center text-sm text-gray-500 dark:text-gray-400">
            {{ project.images.length }} images
          </p>
        </div>
      </div>
    </div>

    <CoolLightBox :items="project.images.map(image => image.src)" :index="index" @close="index = null" />

    <!-- Project info -->
    <div class="pb-20 pt-8">
      <div class="container mx-auto">
        <div class="mx-auto grid max-w-7xl gap-10 lg:grid-cols-3">
          <aside class="space-y-8 lg:sticky lg:top-28 lg:self-start">
            <div v-if="project.aboutClient">
              <h2 class="mb-2 text-xl font-medium text-secondary-dark dark:text-secondary-light">
                About Client
              </h2>
              <p class="text-primary-dark dark:text-ternary-light">
                {{ project.aboutClient }}
              </p>
            </div>

            <div v-if="project.objective">
              <h2 class="mb-2 text-xl font-medium text-secondary-dark dark:text-secondary-light">
                Objective
              </h2>
              <p class="text-primary-dark dark:text-ternary-light">
                {{ project.objective }}
              </p>
            </div>

            <div v-if="project.stack?.length">
              <h2 class="mb-3 text-xl font-medium text-secondary-dark dark:text-secondary-light">
                Stack
              </h2>
              <div class="flex flex-wrap gap-2">
                <span v-for="tech in project.stack" :key="tech" class="rounded-full bg-accent/10 px-3 py-1 text-sm font-medium text-accent">{{ tech }}</span>
              </div>
            </div>

            <div class="text-primary-dark dark:text-ternary-light">
              <h2 class="mb-3 text-xl font-medium">
                Share This
              </h2>
              <div class="flex items-center gap-3">
                <a
                  v-for="share in shareLinks"
                  :key="share.label"
                  :href="share.href"
                  target="_blank"
                  rel="noopener noreferrer"
                  :aria-label="`Share on ${share.label}`"
                  class="rounded-md bg-gray-500 px-3 py-2.5 text-white shadow-sm hover:bg-gray-600"
                >
                  <component :is="share.icon" class="size-4" />
                </a>
              </div>
            </div>
          </aside>

          <div class="markdown text-lg text-ternary-dark dark:text-ternary-light lg:col-span-2" v-html="renderMarkdown(project.content)" />
        </div>
      </div>
    </div>

    <div v-if="hasRelated" class="pb-20">
      <div class="container mx-auto">
        <div class="mx-auto max-w-7xl">
          <ProjectRelatedProjects :current-project="project" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { PhCaretLeft, PhCaretRight, PhClock, PhFacebookLogo, PhFolderOpen, PhLink, PhLinkedinLogo, PhTag, PhTwitterLogo } from '@phosphor-icons/vue'
import ProjectRelatedProjects from '~/components/Projects/ProjectRelatedProjects.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'
import StoreButtons from '~/components/UI/StoreButtons.vue'
import CoolLightBox from '~/components/Vendor/cool-lightbox/CoolLightBox.vue'
import { renderMarkdown } from '~/utils/markdown'
import { findProject, formatDate, PROJECT_TYPE_LABELS, relatedProjects } from '~/utils/projects'

const route = useRoute()
const project = findProject(route.params.slug as string)

if (!project) {
  throw createError({ statusCode: 404, statusMessage: 'Project not found', fatal: true })
}

const index = ref<number | null>(null)
const gallery = ref<HTMLElement | null>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

function updateArrows () {
  const el = gallery.value
  if (!el) { return }
  canScrollPrev.value = el.scrollLeft > 8
  canScrollNext.value = el.scrollLeft + el.clientWidth < el.scrollWidth - 8
}

function scrollGallery (direction: 1 | -1) {
  const el = gallery.value
  el?.scrollBy({ left: direction * el.clientWidth * 0.8, behavior: 'smooth' })
}
const shareUrl = ref('')
const hasStoreLinks = !!(project.links?.appStore || project.links?.googlePlay)
const hasRelated = relatedProjects(project).length > 0

const projectMetaInfo = computed(() => {
  const meta: { icon: any, text: string, url?: string }[] = [
    { icon: PhClock, text: formatDate(project.date) },
    { icon: PhTag, text: PROJECT_TYPE_LABELS[project.type] },
  ]
  if (project.url) {
    meta.push({ icon: PhLink, text: 'Visit Project', url: project.url })
  }
  return meta
})

const shareLinks = computed(() => {
  const url = encodeURIComponent(shareUrl.value || `https://vedware.com/projects/${project.slug}`)
  return [
    { label: 'Facebook', icon: PhFacebookLogo, href: `https://www.facebook.com/sharer/sharer.php?u=${url}` },
    { label: 'Twitter', icon: PhTwitterLogo, href: `https://twitter.com/intent/tweet?url=${url}&text=${encodeURIComponent(project.name)}` },
    { label: 'LinkedIn', icon: PhLinkedinLogo, href: `https://www.linkedin.com/sharing/share-offsite/?url=${url}` },
  ]
})

onMounted(() => {
  shareUrl.value = window.location.href
  updateArrows()
  window.addEventListener('resize', updateArrows)
})

onBeforeUnmount(() => window.removeEventListener('resize', updateArrows))
</script>

<style lang="scss" scoped>
.gallery-arrow {
  @apply absolute top-1/2 z-10 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-primary-dark shadow-soft-lg transition hover:scale-110 hover:text-accent dark:bg-secondary-dark dark:text-white;
}
</style>

<style lang="scss">
.markdown {
  h1 {
    @apply mb-2 text-3xl text-secondary-dark dark:text-secondary-light font-medium;
  }

  h4 {
    @apply mb-2 text-2xl text-secondary-dark dark:text-secondary-light font-medium;
  }

  ul {
    @apply list-disc pl-4 mb-4;
  }

  p {
    @apply mb-4;
  }

  code {
    @apply text-base bg-gray-200 dark:bg-gray-600 rounded py-0.5 px-1;
  }

  a {
    @apply underline;
  }
}
</style>
